import { createContext, useContext } from 'react';
import type { UtilisateurPublic } from '@rh/shared';

/**
 * - `chargement` : reprise de la session au démarrage ;
 * - `connecte` : session ouverte (éventuellement fragile, voir `sessionFragile`) ;
 * - `sans_reseau` : démarrage sans réseau, la session reprendra seule à son retour ;
 * - `session_perdue` : l'API a refusé le renouvellement (révoquée, expirée, compte inactif) ;
 * - `deconnecte` : aucune session, ou déconnexion volontaire.
 */
export type EtatSession = 'chargement' | 'connecte' | 'sans_reseau' | 'session_perdue' | 'deconnecte';

export interface EtatAuth {
  utilisateur: UtilisateurPublic | null;
  chargement: boolean;
  etat: EtatSession;
  /** Renouvellement en panne réseau : l'utilisateur reste connecté, les essais continuent. */
  sessionFragile: boolean;
  /** Message de l'API quand la session a pris fin (`session_perdue`). */
  motifFin: string | null;
  /** Déconnecté sur ce téléphone, pas encore sur le serveur (réseau absent). */
  deconnexionDifferee: boolean;
  connecter: (email: string, motDePasse: string) => Promise<void>;
  /** Bas niveau : ferme la session sans toucher à la file (l'interface passe par useDeconnexion). */
  deconnecter: () => Promise<void>;
  /** Nouvel essai immédiat : reprise sans réseau, renouvellement fragile ou déconnexion différée. */
  reessayer: () => void;
}

export const ContexteAuth = createContext<EtatAuth | null>(null);

export function useAuth(): EtatAuth {
  const contexte = useContext(ContexteAuth);
  if (!contexte) throw new Error('useAuth hors du fournisseur');
  return contexte;
}
